import type { AnalyticsOverview } from '@rp/contracts';

/**
 * Короткий вывод по цифрам: что происходит и стоит ли вмешиваться.
 *
 * Нужен и на главной, и в аналитике, поэтому живёт отдельно от экранов. Смысл в
 * том, чтобы владелец понимал состояние без чтения графиков: набор чисел сам по
 * себе не отвечает на вопрос «всё ли в порядке».
 */

export type Verdict = 'good' | 'watch' | 'bad';

export interface Insight {
  readonly verdict: Verdict;
  readonly title: string;
  readonly detail: string;
  /** Куда вести по нажатию — строка запроса ленты. */
  readonly filter: Record<string, string> | null;
}

const VERDICT_COLOR: Record<Verdict, string> = {
  good: 'var(--color-positive)',
  watch: 'var(--color-neutral)',
  bad: 'var(--color-negative)',
};

export const verdictColor = (verdict: Verdict): string => VERDICT_COLOR[verdict];

/** Доля негатива среди классифицированных отзывов. */
export function negativeShare(overview: AnalyticsOverview): number {
  const total = overview.sentimentSplit.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) return 0;
  const negative = overview.sentimentSplit.find((item) => item.sentiment === 'NEGATIVE')?.count ?? 0;
  return negative / total;
}

/**
 * Главный вывод периода.
 *
 * Порядок проверок — от того, что требует действия прямо сейчас, к общему фону:
 * неотвеченные важнее среднего балла, потому что их можно исправить сегодня.
 */
export function mainInsight(overview: AnalyticsOverview): Insight {
  const { totals, deltas } = overview;

  if (totals.reviews === 0) {
    return {
      verdict: 'watch',
      title: 'Отзывов за период нет',
      detail: 'Как только они появятся, здесь будет видно динамику.',
      filter: null,
    };
  }

  if (totals.unanswered > 0) {
    return {
      verdict: totals.unanswered > 5 ? 'bad' : 'watch',
      title: `${totals.unanswered} без ответа`,
      detail: 'Неотвеченные отзывы снижают доверие и позицию в выдаче площадок.',
      filter: { statuses: 'NEW,NEEDS_REPLY,DRAFT_READY' },
    };
  }

  if (deltas.avgRating <= -0.2) {
    return {
      verdict: 'bad',
      title: 'Оценка падает',
      detail: `Средний балл снизился на ${Math.abs(deltas.avgRating).toFixed(2)} к прошлому периоду.`,
      filter: { ratings: '1,2,3' },
    };
  }

  if (negativeShare(overview) > 0.25) {
    return {
      verdict: 'watch',
      title: 'Много негатива',
      detail: 'Каждый четвёртый отзыв — отрицательный. Загляните в темы жалоб.',
      filter: { sentiment: 'NEGATIVE' },
    };
  }

  if (totals.avgRating >= 4.5) {
    return {
      verdict: 'good',
      title: 'Всё хорошо',
      detail: `Средняя оценка ${totals.avgRating.toFixed(2)}, все отзывы отвечены.`,
      filter: null,
    };
  }

  return {
    verdict: 'watch',
    title: 'Есть над чем поработать',
    detail: `Средняя оценка ${totals.avgRating.toFixed(2)}. Посмотрите, на что жалуются чаще всего.`,
    filter: { ratings: '1,2,3' },
  };
}

/** Темы, разложенные на похвалу и жалобы: с ними работают по-разному. */
export function splitTopics(overview: AnalyticsOverview) {
  const complaints = overview.topTopics
    .filter((topic) => topic.sentiment === 'NEGATIVE' || topic.avgRating < 4)
    .sort((a, b) => a.avgRating - b.avgRating || b.count - a.count);

  const praise = overview.topTopics
    .filter((topic) => !complaints.includes(topic))
    .sort((a, b) => b.count - a.count);

  return { complaints, praise };
}
